import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Paper, Box, Typography, Button, Chip, Grid, Divider, Alert, AlertTitle,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Stepper, Step, StepLabel,
  styled, Menu, MenuItem, ListItemIcon, ListItemText,
} from '@mui/material';
import { getOffer, convertOfferToInvoice, updateOfferStatus } from '../services/offerService';
import { toast } from 'react-toastify';
import { OFFER_STATUS, canEditOffer, canConvertOffer, getStatusColor, getStatusLabel } from '../../../constants/offerConstants';
import TransformIcon from '@mui/icons-material/Transform';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SendIcon from '@mui/icons-material/Send';
import DraftsIcon from '@mui/icons-material/Drafts';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import StepConnector, { stepConnectorClasses } from '@mui/material/StepConnector';

// ✅ Custom Stepper Connector with dynamic colors
const ColorlibConnector = styled(StepConnector)(({ theme, ownerState }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 22,
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage: 'linear-gradient(95deg, #667eea 0%, #764ba2 100%)',
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      backgroundImage: 'linear-gradient(95deg, #4caf50 0%, #388e3c 100%)',
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    border: 0,
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#eaeaf0',
    borderRadius: 1,
  },
}));

// ✅ Custom Step Icon with colors
const ColorlibStepIconRoot = styled('div')(({ theme, ownerState }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : '#ccc',
  zIndex: 1,
  color: '#fff',
  width: 50,
  height: 50,
  display: 'flex',
  borderRadius: '50%',
  justifyContent: 'center',
  alignItems: 'center',
  boxShadow: '0 4px 10px 0 rgba(0,0,0,.25)',
  ...(ownerState.active && {
    backgroundImage: 'linear-gradient(136deg, #667eea 0%, #764ba2 100%)',
    boxShadow: '0 4px 10px 0 rgba(0,0,0,.25)',
  }),
  ...(ownerState.completed && {
    backgroundImage: 'linear-gradient(136deg, #4caf50 0%, #388e3c 100%)',
  }),
  ...(ownerState.rejected && {
    backgroundImage: 'linear-gradient(136deg, #f44336 0%, #d32f2f 100%)',
  }),
}));

function ColorlibStepIcon(props) {
  const { active, completed, className, icon, rejected } = props;

  const icons = {
    1: <DraftsIcon />,
    2: <SendIcon />,
    3: rejected ? <ThumbDownIcon /> : <ThumbUpIcon />,
    4: <ReceiptIcon />,
  };

  return (
    <ColorlibStepIconRoot ownerState={{ completed, active, rejected }} className={className}>
      {icons[String(icon)]}
    </ColorlibStepIconRoot>
  );
}

const OfferDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState(null); // ✅ For status change menu

  useEffect(() => {
    fetchOffer();
  }, [id]);

  const fetchOffer = async () => {
    try {
      const response = await getOffer(id);
      console.log('✅ Raw response from getOffer:', response);
      
      // ✅ Your API returns { payload: {...} } structure
      const offerData = response.payload || response;
      console.log('✅ Extracted offer data:', offerData);
      
      // ✅ Map the offer data properly
      const mappedOffer = {
        ...offerData,
        CustomerName: offerData.Customer?.Name || 'N/A',
        CustomerEmail: offerData.Customer?.Email || 'N/A',
        CustomerPhone: offerData.Customer?.PhoneNumber || offerData.Customer?.MobilePhone || 'N/A',
        CustomerAddress: `${offerData.Customer?.Street || ''} ${offerData.Customer?.Box || ''}, ${offerData.Customer?.PostCode || ''} ${offerData.Customer?.City || ''}, ${offerData.Customer?.Country || ''}`.trim(),
        // ✅ Calculate totals from line items
        TotalExcl: offerData.LineItems?.reduce((sum, item) => sum + (item.TotalExcl || (item.Quantity * item.UnitPrice)), 0) || 0,
        TotalVAT: offerData.LineItems?.reduce((sum, item) => {
          const excl = item.TotalExcl || (item.Quantity * item.UnitPrice);
          return sum + (excl * item.VAT) / 100;
        }, 0) || 0,
        TotalIncl: offerData.LineItems?.reduce((sum, item) => {
          const excl = item.TotalExcl || (item.Quantity * item.UnitPrice);
          const vat = (excl * item.VAT) / 100;
          return sum + excl + vat;
        }, 0) || 0,
        // ✅ Map line items with product details
        LineItems: offerData.LineItems?.map(item => ({
          ...item,
          ProductName: item.Product?.Name || `Product #${item.ProductId}`,
          ProductSKU: item.Product?.SKU || '',
          ProductDescription: item.Product?.Description || '',
        })) || [],
      };
      
      console.log('✅ Mapped offer with totals:', mappedOffer);
      setOffer(mappedOffer);
    } catch (error) {
      console.error('❌ Error fetching offer:', error);
      toast.error('Failed to load offer');
      navigate('/sales/offers');
    } finally {
      setLoading(false);
    }
  };

  const handleConvert = async () => {
    if (!canConvertOffer(offer.Status)) {
      toast.error('Only accepted offers can be converted');
      return;
    }

    if (window.confirm('Convert this offer to an invoice? This cannot be undone.')) {
      try {
        const result = await convertOfferToInvoice(id);
        toast.success(`Invoice #${result.InvoiceNumber} created!`);
        navigate(`/invoices/view/${result.InvoiceId}`);
      } catch (error) {
        toast.error(error.message || 'Failed to convert offer');
      }
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    try {
      await updateOfferStatus(id, newStatus);
      toast.success(`Offer status updated to ${getStatusLabel(newStatus)}`);
      setAnchorEl(null); // ✅ Close menu
      fetchOffer();
    } catch (error) {
      toast.error(error.message || 'Failed to update status');
    }
  };

  // ✅ Open/Close status menu
  const handleOpenStatusMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseStatusMenu = () => {
    setAnchorEl(null);
  };

  // ✅ Get available status transitions based on current status
  const getAvailableStatuses = (currentStatus) => {
    const statuses = [];
    
    // Can't change status if already converted
    if (currentStatus === OFFER_STATUS.CONVERTED) {
      return [];
    }

    // Draft can go to Sent
    if (currentStatus === OFFER_STATUS.DRAFT) {
      statuses.push({ value: OFFER_STATUS.SENT, label: 'Sent', icon: <SendIcon />, color: '#2196f3' });
    }

    // Sent can go to Accepted or Rejected
    if (currentStatus === OFFER_STATUS.SENT) {
      statuses.push({ value: OFFER_STATUS.ACCEPTED, label: 'Accepted', icon: <CheckCircleIcon />, color: '#4caf50' });
      statuses.push({ value: OFFER_STATUS.REJECTED, label: 'Rejected', icon: <CancelIcon />, color: '#f44336' });
    }

    // Accepted can go back to Sent or be Rejected
    if (currentStatus === OFFER_STATUS.ACCEPTED) {
      statuses.push({ value: OFFER_STATUS.SENT, label: 'Sent', icon: <SendIcon />, color: '#2196f3' });
      statuses.push({ value: OFFER_STATUS.REJECTED, label: 'Rejected', icon: <CancelIcon />, color: '#f44336' });
    }

    // Rejected can go back to Sent or Accepted
    if (currentStatus === OFFER_STATUS.REJECTED) {
      statuses.push({ value: OFFER_STATUS.SENT, label: 'Sent', icon: <SendIcon />, color: '#2196f3' });
      statuses.push({ value: OFFER_STATUS.ACCEPTED, label: 'Accepted', icon: <CheckCircleIcon />, color: '#4caf50' });
    }

    return statuses;
  };

  // ✅ Get active step based on status
  const getActiveStep = (status) => {
    if (status === OFFER_STATUS.REJECTED) return 2;
    if (status === OFFER_STATUS.CONVERTED) return 3;
    if (status === OFFER_STATUS.ACCEPTED) return 2;
    if (status === OFFER_STATUS.SENT) return 1;
    return 0;
  };

  // ✅ Determine which steps are completed
  const isStepCompleted = (step, currentStatus) => {
    if (currentStatus === OFFER_STATUS.REJECTED) {
      return step < 2;
    }
    if (currentStatus === OFFER_STATUS.CONVERTED) return step < 3;
    if (currentStatus === OFFER_STATUS.ACCEPTED) return step < 2;
    if (currentStatus === OFFER_STATUS.SENT) return step < 1;
    return false;
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography>Loading offer details...</Typography>
        </Paper>
      </Container>
    );
  }

  if (!offer) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography>Offer not found</Typography>
          <Button onClick={() => navigate('/sales/offers')} sx={{ mt: 2 }}>
            Back to Offers
          </Button>
        </Paper>
      </Container>
    );
  }

  const steps = ['Draft', 'Sent', offer.Status === OFFER_STATUS.REJECTED ? 'Rejected' : 'Accepted', 'Converted'];
  const activeStep = getActiveStep(offer.Status);
  const isRejected = offer.Status === OFFER_STATUS.REJECTED;
  const availableStatuses = getAvailableStatuses(offer.Status);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="h4" fontWeight="700" sx={{ color: '#667eea' }}>
              Offer #{offer.OfferNumber}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Created: {new Date(offer.OfferDate || offer.CreatedDate).toLocaleDateString()}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/sales/offers')}
              variant="outlined"
            >
              Back
            </Button>

            {/* ✅ Edit - Only for Draft/Sent */}
            {canEditOffer(offer.Status) && (
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => navigate(`/sales/offers/edit/${id}`)}
              >
                Edit
              </Button>
            )}

            {/* ✅ Change Status Button - Show if transitions available */}
            {availableStatuses.length > 0 && (
              <>
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<ChangeCircleIcon />}
                  endIcon={<KeyboardArrowDownIcon />}
                  onClick={handleOpenStatusMenu}
                  sx={{
                    background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #f57c00 0%, #e65100 100%)',
                    },
                  }}
                >
                  Change Status
                </Button>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleCloseStatusMenu}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  PaperProps={{
                    sx: {
                      mt: 1,
                      minWidth: 200,
                      borderRadius: 2,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  <Box sx={{ px: 2, py: 1, borderBottom: '1px solid #e0e0e0' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      Change Status To:
                    </Typography>
                  </Box>
                  {availableStatuses.map((status) => (
                    <MenuItem
                      key={status.value}
                      onClick={() => handleStatusUpdate(status.value)}
                      sx={{
                        py: 1.5,
                        '&:hover': {
                          backgroundColor: `${status.color}15`,
                        },
                      }}
                    >
                      <ListItemIcon sx={{ color: status.color }}>
                        {status.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={status.label}
                        primaryTypographyProps={{
                          fontWeight: 600,
                          color: status.color,
                        }}
                      />
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}

            {/* ✅ Convert - Only for Accepted */}
            {canConvertOffer(offer.Status) && (
              <Button
                variant="contained"
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                }}
                startIcon={<TransformIcon />}
                onClick={handleConvert}
              >
                Convert to Invoice
              </Button>
            )}
          </Box>
        </Box>

        {/* ✅ Status Breadcrumb Stepper */}
        <Box sx={{ width: '100%', mt: 3 }}>
          <Stepper 
            alternativeLabel 
            activeStep={activeStep} 
            connector={<ColorlibConnector />}
            sx={{
              '& .MuiStepLabel-label': {
                mt: 1,
                fontSize: '0.95rem',
                fontWeight: 600,
              },
              '& .MuiStepLabel-label.Mui-active': {
                color: '#667eea',
                fontWeight: 700,
              },
              '& .MuiStepLabel-label.Mui-completed': {
                color: '#4caf50',
                fontWeight: 700,
              },
            }}
          >
            {steps.map((label, index) => {
              const stepProps = {};
              const labelProps = {};
              
              // Mark rejected step differently
              if (index === 2 && isRejected) {
                labelProps.error = true;
                labelProps.sx = { color: '#f44336 !important' };
              }

              return (
                <Step key={label} {...stepProps} completed={isStepCompleted(index, offer.Status)}>
                  <StepLabel 
                    {...labelProps}
                    StepIconComponent={(props) => (
                      <ColorlibStepIcon {...props} rejected={index === 2 && isRejected} />
                    )}
                  >
                    {label}
                  </StepLabel>
                </Step>
              );
            })}
          </Stepper>
        </Box>
      </Paper>

      {/* ✅ Status-specific alerts */}
      {offer.Status === OFFER_STATUS.CONVERTED && (
        <Alert 
          severity="info" 
          sx={{ 
            mb: 3,
            borderRadius: 2,
            '& .MuiAlert-icon': {
              color: '#673ab7',
            },
            background: 'linear-gradient(135deg, rgba(103, 58, 183, 0.1) 0%, rgba(103, 58, 183, 0.05) 100%)',
            border: '1px solid rgba(103, 58, 183, 0.3)',
          }}
        >
          <AlertTitle sx={{ fontWeight: 700 }}>✓ Converted to Invoice</AlertTitle>
          This offer has been converted to Invoice #{offer.InvoiceNumber}. It can no longer be modified.
          <Button
            size="small"
            onClick={() => navigate(`/invoices/view/${offer.InvoiceId}`)}
            sx={{ ml: 2, mt: 1 }}
            variant="outlined"
          >
            View Invoice
          </Button>
        </Alert>
      )}

      {offer.Status === OFFER_STATUS.REJECTED && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3,
            borderRadius: 2,
            background: 'linear-gradient(135deg, rgba(244, 67, 54, 0.1) 0%, rgba(244, 67, 54, 0.05) 100%)',
            border: '1px solid rgba(244, 67, 54, 0.3)',
          }}
        >
          <AlertTitle sx={{ fontWeight: 700 }}>✗ Offer Rejected</AlertTitle>
          This offer has been rejected by the customer.
        </Alert>
      )}

      {offer.Status === OFFER_STATUS.ACCEPTED && (
        <Alert 
          severity="success" 
          sx={{ 
            mb: 3,
            borderRadius: 2,
            background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(76, 175, 80, 0.05) 100%)',
            border: '1px solid rgba(76, 175, 80, 0.3)',
          }}
        >
          <AlertTitle sx={{ fontWeight: 700 }}>✓ Offer Accepted</AlertTitle>
          This offer has been accepted! You can now convert it to an invoice.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Customer Information */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)', height: '100%' }}>
            <Typography variant="h6" fontWeight="700" sx={{ mb: 2, color: '#667eea' }}>
              Customer Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Name</Typography>
                <Typography variant="body1" fontWeight={500}>{offer.CustomerName}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                <Typography variant="body1">{offer.CustomerEmail}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
                <Typography variant="body1">{offer.CustomerPhone}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Address</Typography>
                <Typography variant="body1">{offer.CustomerAddress}</Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Offer Summary */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)', height: '100%' }}>
            <Typography variant="h6" fontWeight="700" sx={{ mb: 2, color: '#667eea' }}>
              Offer Summary
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Current Status</Typography>
                <Chip
                  label={getStatusLabel(offer.Status)}
                  sx={{
                    mt: 1,
                    backgroundColor: getStatusColor(offer.Status),
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    px: 2,
                    py: 2.5,
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">Subtotal (Excl. VAT)</Typography>
                <Typography variant="h6" fontWeight={600}>€{offer.TotalExcl.toFixed(2)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">VAT Amount</Typography>
                <Typography variant="h6" fontWeight={600}>€{offer.TotalVAT.toFixed(2)}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Total (Incl. VAT)</Typography>
                <Typography variant="h4" fontWeight={700} sx={{ color: '#667eea' }}>
                  €{offer.TotalIncl.toFixed(2)}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Line Items */}
        <Grid item xs={12}>
          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>
            <Typography variant="h6" fontWeight="700" sx={{ mb: 2, color: '#667eea' }}>
              Line Items
            </Typography>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'rgba(102, 126, 234, 0.1)' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>SKU</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Quantity</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Unit Price</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>VAT %</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">Total (Excl.)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {offer.LineItems?.map((item, index) => (
                    <TableRow key={index} hover>
                      <TableCell>
                        <Typography fontWeight={500}>{item.ProductName}</Typography>
                        {item.ProductDescription && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {item.ProductDescription}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {item.ProductSKU && (
                          <Chip 
                            label={item.ProductSKU} 
                            size="small" 
                            variant="outlined"
                            sx={{ fontSize: '0.75rem' }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={item.Quantity} 
                          size="small" 
                          color="info"
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight={500}>€{item.UnitPrice.toFixed(2)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={`${item.VAT}%`} 
                          size="small" 
                          variant="outlined"
                          color="primary"
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: '#667eea' }}>
                        €{item.TotalExcl.toFixed(2)}
                      </TableCell>
                    </TableRow>
                  ))}
                  
                  {/* ✅ Totals Row */}
                  <TableRow sx={{ bgcolor: 'rgba(102, 126, 234, 0.05)' }}>
                    <TableCell colSpan={5} align="right" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                      Subtotal (Excl. VAT):
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1rem', color: '#667eea' }}>
                      €{offer.TotalExcl.toFixed(2)}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ bgcolor: 'rgba(102, 126, 234, 0.05)' }}>
                    <TableCell colSpan={5} align="right" sx={{ fontWeight: 700, fontSize: '1rem' }}>
                      VAT:
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1rem', color: '#667eea' }}>
                      €{offer.TotalVAT.toFixed(2)}
                    </TableCell>
                  </TableRow>
                  <TableRow sx={{ bgcolor: 'rgba(102, 126, 234, 0.15)' }}>
                    <TableCell colSpan={5} align="right" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
                      Total (Incl. VAT):
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#667eea' }}>
                      €{offer.TotalIncl.toFixed(2)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Notes */}
        {offer.Notes && offer.Notes.trim() !== '' && (
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)' }}>
              <Typography variant="h6" fontWeight="700" sx={{ mb: 2, color: '#667eea' }}>
                Notes
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {offer.Notes}
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Container>
  );
};

export default OfferDetailPage;